import { useEffect, useState, useCallback } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Filter, Loader2, Stethoscope, Search, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TherapistCard } from '@/components/TherapistCard';

const EMPTY = { state: [], specialty: [], modality: [], insurance: [], language: [] };

export default function PublicDirectory() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ ...EMPTY });
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const parts = [];
      Object.entries(filters).forEach(([k, arr]) => (arr || []).forEach((v) => parts.push(`${k}=${encodeURIComponent(v)}`)));
      const term = q.trim();
      if (term) parts.push(`q=${encodeURIComponent(term)}`);
      const r = await http.get(`/therapists${parts.length ? '?' + parts.join('&') : ''}`);
      setData(r.data);
    } catch { toast.error('Could not load the directory'); }
    finally { setLoading(false); }
  }, [filters, q]);

  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); }, [load]);

  const facets = data?.facets || { states: [], specialties: [], insurance: [], languages: [], modalities: [] };
  const activeCount = Object.values(filters).reduce((a, arr) => a + (arr?.length || 0), 0);
  const hasDirectory = (data?.items?.length || 0) > 0 || activeCount > 0 || (facets.states.length + facets.specialties.length) > 0;
  const toggle = (dim, value) => setFilters((f) => {
    const cur = f[dim] || [];
    return { ...f, [dim]: cur.includes(value) ? cur.filter((x) => x !== value) : [...cur, value] };
  });
  const clearAll = () => setFilters({ ...EMPTY });

  const DIMS = [
    { key: 'state', label: 'State', facet: 'states', fmt: (v) => v },
    { key: 'specialty', label: 'Specialty', facet: 'specialties', fmt: (v) => v },
    { key: 'modality', label: 'Format', facet: 'modalities', fmt: (v) => (v === 'telehealth' ? 'Telehealth' : v === 'in-person' ? 'In-person' : v) },
    { key: 'insurance', label: 'Insurance', facet: 'insurance', fmt: (v) => v },
    { key: 'language', label: 'Language', facet: 'languages', fmt: (v) => v },
  ];

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="public-directory-page">
      {/* Public header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-navy/10">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <button onClick={() => nav('/')} className="flex items-center gap-2" data-testid="dir-home">
            <span className="w-8 h-8 rounded-lg bg-navy text-gold flex items-center justify-center font-heading text-sm">B+</span>
            <span className="font-heading text-lg text-navy hidden sm:inline">Brotherhood+</span>
          </button>
          <div className="flex items-center gap-2">
            <button onClick={() => nav('/therapist-apply')} data-testid="dir-apply" className="text-sm text-navy/70 hover:text-navy rounded-full px-3 py-2 hidden sm:inline">Are you a therapist?</button>
            <button onClick={() => nav('/login')} data-testid="dir-join" className="bg-navy text-white rounded-full px-4 py-2 text-sm font-medium hover:bg-navy-soft">Join Brotherhood+</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 space-y-6 pb-24">
        <button onClick={() => nav('/')} className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy"><ArrowLeft size={14}/> Back to home</button>

        <div className="bg-navy text-white rounded-3xl p-8 lg:p-12 card-soft">
          <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold inline-flex items-center gap-2"><Stethoscope size={14}/> Free Therapist Directory</p>
          <h1 className="font-heading text-3xl lg:text-5xl mt-2 leading-tight">Culturally responsive care.</h1>
          <p className="text-white/75 text-base lg:text-lg mt-4 max-w-3xl">
            Browse licensed mental health professionals skilled in culturally responsive care for men of every background — free, no account needed. Every provider is vetted before they appear here.
          </p>
        </div>

        {loading && <div className="text-center py-10 text-navy/45"><Loader2 className="inline animate-spin" size={20}/></div>}

        {!loading && !hasDirectory && (
          <div className="bg-white rounded-3xl p-10 text-center card-soft" data-testid="dir-empty">
            <p className="font-heading text-xl">Our vetted directory is launching soon.</p>
            <p className="text-navy/65 mt-2">Check back shortly — new providers are being added.</p>
            <button onClick={() => nav('/login')} className="mt-5 bg-navy text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-navy-soft">Join Brotherhood+</button>
          </div>
        )}

        {!loading && hasDirectory && (
          <>
            <div className="relative" data-testid="dir-search-wrap">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/40 pointer-events-none"/>
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by name, specialty, or keyword"
                data-testid="dir-search-input"
                className="w-full bg-white rounded-2xl card-soft pl-11 pr-11 py-3.5 text-sm text-navy placeholder:text-navy/40 focus:outline-none focus:ring-2 focus:ring-gold/40"
              />
              {q && (
                <button onClick={() => setQ('')} data-testid="dir-search-clear" aria-label="Clear search"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-navy/40 hover:text-navy rounded-full p-1">
                  <X size={16}/>
                </button>
              )}
            </div>

            <div className="bg-white rounded-2xl p-4 card-soft" data-testid="dir-filters">
              <div className="flex items-center gap-2 mb-3">
                <Filter size={14} className="text-navy/60"/>
                <span className="text-xs uppercase tracking-widest font-semibold text-navy/60">Filter</span>
                {activeCount > 0 && <button onClick={clearAll} className="ml-auto text-xs text-gold hover:underline" data-testid="dir-clear">Clear all</button>}
              </div>
              <div className="space-y-3">
                {DIMS.map(({ key, label, facet, fmt }) => {
                  const opts = facets[facet] || [];
                  if (!opts.length) return null;
                  return (
                    <div key={key}>
                      <p className="text-[11px] uppercase tracking-widest text-navy/45 font-semibold mb-1.5">{label}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {opts.map((o) => {
                          const on = (filters[key] || []).includes(o.value);
                          return (
                            <button key={o.value} onClick={() => toggle(key, o.value)} data-testid={`dir-filter-${key}-${o.value}`}
                              className={`text-xs rounded-full px-3 py-1.5 transition-smooth ${on ? 'bg-navy text-white' : 'bg-[#F9F8F6] text-navy/70 hover:bg-beige'}`}>
                              {fmt(o.value)} <span className={on ? 'text-white/70' : 'text-navy/40'}>({o.count})</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <p className="text-sm text-navy/55">{data.total} therapist{data.total === 1 ? '' : 's'}</p>

            {data.items.length === 0 ? (
              <div className="bg-white rounded-2xl p-10 text-center card-soft" data-testid="dir-no-match">
                <p className="font-heading text-lg">No matches{q ? ` for “${q}”` : ' with these filters'}.</p>
                <button onClick={() => { clearAll(); setQ(''); }} className="mt-3 text-sm text-gold hover:underline">Clear search &amp; filters</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {data.items.map((t) => <TherapistCard key={t.id} t={t}/>)}
              </div>
            )}
          </>
        )}

        <p className="text-xs text-navy/50 text-center pt-2">
          Brotherhood+ lists independent providers and is not a referral service or medical provider. Verify credentials with your state board before booking.
        </p>
      </div>
    </div>
  );
}
